/* ============================================================================
 * useImportExport.ts - Import/export operations for project data
 * ============================================================================ */

import { useCallback, useEffect, useRef, type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import type { ProjectData } from '@/shared/types/projectData';
import { useMultiProject } from '@/features/projects/MultiProjectContext';
import { log } from '@/shared/utils/logger';
import { authService } from '@/features/auth/services/authService';
import { API_ENDPOINTS } from '@/shared/apiClient';
import i18n from '@/shared/i18n/config';

function importErrorReason(body: string, fallback: string): string {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const data = parsed as Record<string, unknown>;
      const errors = Array.isArray(data.validationErrors)
        ? data.validationErrors.filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
        : [];
      if (errors.length) return errors.join(' ');
      for (const key of ['details', 'detail', 'error', 'message']) {
        const value = data[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
      return fallback;
    }
  } catch {
    // Plain-text upstream errors are also supported.
  }
  return body.trim().slice(0, 500) || fallback;
}

interface UseImportExportParams {
  setError: Dispatch<SetStateAction<string | null>>;
  sessionId: MutableRefObject<string>;
}

/**
 * TeamGantt import: creates a new project on the server and selects it.
 */
export function useImportExport({
  setError,
  sessionId,
}: UseImportExportParams) {
  const {
    loadProjects,
    selectProjects,
    selectedProjectIds,
    activeProjectId,
  } = useMultiProject();
  const mountedRef = useRef(true);
  const selectionKey = JSON.stringify([selectedProjectIds, activeProjectId, sessionId.current]);
  const selectionRef = useRef({ key: selectionKey, epoch: 0 });
  if (selectionRef.current.key !== selectionKey) {
    selectionRef.current = { key: selectionKey, epoch: selectionRef.current.epoch + 1 };
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // TeamGantt import creates a new project; it never changes the currently open project.
  const importProjectData = useCallback(async (data: ProjectData) => {
    try {
      // Validate the data structure
      if (!data.nodes || !Array.isArray(data.nodes) || !Array.isArray(data.rootNodeIds)) {
        throw new Error(i18n.t('Invalid project data: missing or invalid nodes/root nodes arrays'));
      }

      const selectionAtStart = selectionRef.current;

      // Send import to server API (TeamGantt import endpoint)
      const payload = {
        projectData: data,
        source: 'TeamGantt CSV Import',
        timestamp: new Date().toISOString()
      };

      log.debug('Import', 'Import payload size:', JSON.stringify(payload).length, 'bytes');

      const response = await authService.authenticatedFetch(API_ENDPOINTS.import.teamgantt, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        log.error('Import', 'Import error response:', {
          status: response.status,
          statusText: response.statusText,
        });

        // Special handling for authentication errors
        if (response.status === 401) {
          throw new Error(i18n.t('Authentication failed. Please log in again and retry the import.'));
        }
        if (response.status === 403) {
          throw new Error(i18n.t('You do not have permission to import projects.'));
        }

        throw new Error(i18n.t('Import failed: {{reason}}', { reason: importErrorReason(errorBody, response.statusText) }));
      }

      const result: unknown = await response.json();
      log.debug('Import', 'Import response received:', result);

      const importedProjectId = result && typeof result === 'object'
        && 'projectId' in result && typeof result.projectId === 'string'
        ? result.projectId.trim() : '';
      if (!importedProjectId) {
        throw new Error(i18n.t('Import failed: server did not return a canonical project ID.'));
      }
      if (result && typeof result === 'object' && 'success' in result && result.success === false) {
        throw new Error(i18n.t('Import failed: server reported an unsuccessful import.'));
      }
      log.info('Import', `Import successful, project ID: ${importedProjectId}`);

      // One list refresh is enough: the API write is the source of truth. Do
      // not queue a WebSocket mutation or reset current-project sync state.
      try {
        await loadProjects();
        const selectionUnchanged = mountedRef.current
          && selectionRef.current.epoch === selectionAtStart.epoch;
        if (selectionUnchanged) {
          await selectProjects([importedProjectId], 'set');
          if (mountedRef.current) setError(null);
        }
      } catch (selectionError) {
        const warning = i18n.t('Project imported successfully but couldn\'t automatically open it. Please select "{{projectName}}" from the project list.', { projectName: data.projectName });
        log.warn('Import', warning, selectionError);
        if (mountedRef.current) setError(warning);
        return {
          success: true,
          projectId: importedProjectId,
          message: warning,
        };
      }

      // Return success info to caller
      return {
        success: true,
        projectId: importedProjectId,
        // i18n-raw-ui-literal-exempt: Internal import-result metadata; presentation owns its localized message.
        message: 'Project imported successfully'
      };

    } catch (error) {
      log.error('Import', 'Import error:', error);

      const errorMessage = i18n.t('Import issue: {{message}}. Please check if your project was imported.', {
        message: error instanceof Error ? error.message : i18n.t('Unknown error'),
      });
      if (mountedRef.current) setError(errorMessage);

      throw error;
    }
  }, [
    setError,
    loadProjects,
    selectProjects,
  ]);

  return {
    importProjectData
  };
}
